import React, { useState, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaBed } from "react-icons/fa";
import { FaCircleInfo } from "react-icons/fa6";
import { MdOutlineFlight } from "react-icons/md";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import PassengersData from "../../../../Components/PassengersData/PassengersData";
import { useOnClickOutside } from "../../../../utils/ComponentsUtils";
import Supplement from "../../../../Components/InfoBox/Supplement";
import { useAppContext } from "../../../../contexts/AppContext";
import { Loader } from "../../../../Components/Loader/Loader";
import { TermsAndConditions } from "../../../AgentTerms/AgentTerms";
import AdditionalInfo from "../../../../Components/InfoBox/AdditionalInfo";
import BackButton from "../../../../Components/Back/Back";
import { buildOrganizedTourProfileViewModel } from "../../../../utils/buildOrganizedTourProfileViewModel";
import "../OrganizedTourProfilePage.scss";
import OrganizedTourProfileLayout from "../components/OrganizedTourProfileLayout";
import OrganizedTourProfileSection from "../components/OrganizedTourProfileSection";
import OrganizedTourFlightTimeline from "../components/OrganizedTourFlightTimeline";
import OrganizedTourProfileAgentPanel from "../components/OrganizedTourProfileAgentPanel";
import OrganizedTourInfoSection from "../components/OrganizedTourInfoSection";

const DISCLOSURE_ITEMS = [
  "דמי הטיפול / תוספת העמלה של הסוכן אינם כלולים במחיר החדר.",
  "שינוי או ביטול הזמנה כרוכים בדמי טיפול של החברה בסך 85$ לנוסע, בנוסף לדמי הביטול של הספק.",
  "המחיר והזמינות סופיים רק לאחר אישור הספק ושובר.",
  "תנאי הביטול והשינוי נקבעים בלעדית על ידי הספק ועלולים לכלול חיובים מלאים.",
  "ייתכנו אי־דיוקים במידע ובתמונות.",
  "הסוכן מחויב להעביר ללקוח את כל תנאי ההזמנה בצורה מלאה וברורה.",
];

const PackageDetails = memo(
  ({ createCustomerPriceQuote, leftToPay, orgTourInfo, orderId }) => {
    const { isAppLoggedIn, airlines } = useAppContext();
    const disabled = !isAppLoggedIn();
    const {
      selectedResult,
      isConditionsConfirmed,
      setIsConditionsConfirmed,
      handleFeeData,
      updateHandleFeeData,
      agentData,
      hasLdsSupplements,
      ourSavedLDSOrder,
      roomsAndPaxInfo,
      ourPrice,
    } = useSelectedResult();

    const { t } = useTranslation();
    const [isTermsModal, setIsTermsModal] = useState(false);
    const modalRef = React.createRef();
    useOnClickOutside(modalRef, () => setIsTermsModal(false));

    const { clientRemarks, hotel, linkedFlights } = selectedResult || {};

    const viewModel = useMemo(
      () =>
        buildOrganizedTourProfileViewModel({
          selectedResult,
          resourceUrl: orgTourInfo?.resourceUrl,
          ourPrice,
          savedOrder: ourSavedLDSOrder,
        }),
      [selectedResult, orgTourInfo?.resourceUrl, ourPrice, ourSavedLDSOrder],
    );

    if (!selectedResult) return <Loader />;

    const agentPanel =
      !disabled ? (
        <OrganizedTourProfileAgentPanel
          t={t}
          airlines={airlines}
          handleFeeData={handleFeeData}
          updateHandleFeeData={updateHandleFeeData}
          orderId={orderId || ourSavedLDSOrder?.id || ourSavedLDSOrder?.ourLdsId}
          createCustomerPriceQuote={createCustomerPriceQuote}
          ourPrice={ourPrice}
          ourSavedLDSOrder={ourSavedLDSOrder}
          selectedResult={selectedResult}
          roomsAndPaxInfo={roomsAndPaxInfo}
          agentData={agentData}
        />
      ) : null;

    return (
      <>
        <div className="otp-page__back">
          <BackButton />
        </div>

        <OrganizedTourProfileLayout
          viewModel={viewModel}
          roomsAndPaxInfo={roomsAndPaxInfo}
          leftToPay={leftToPay}
          agentPanel={agentPanel}
        >
          <OrganizedTourProfileSection id="otp-section-overview" title="סקירת הטיול">
            <p className="otp-overview-route">{viewModel.routeLabel || viewModel.tourName}</p>
            {viewModel.dateRangeDisplay ? (
              <p className="otp-overview-dates">{viewModel.dateRangeDisplay}</p>
            ) : null}
          </OrganizedTourProfileSection>

          <OrganizedTourInfoSection
            loading={orgTourInfo?.loading}
            error={orgTourInfo?.error}
            missingFields={orgTourInfo?.missingFields}
            contentText={orgTourInfo?.contentText}
            contentHtmlSanitized={orgTourInfo?.contentHtmlSanitized}
            resourceUrl={orgTourInfo?.resourceUrl}
            resourceDescription={orgTourInfo?.resourceDescription}
            resourceFetchOk={orgTourInfo?.resourceFetchOk}
          />

          <OrganizedTourProfileSection
            id="otp-section-flights"
            title="פרטי טיסה"
            icon={MdOutlineFlight}
          >
            <OrganizedTourFlightTimeline linkedFlights={linkedFlights} />
          </OrganizedTourProfileSection>

          <OrganizedTourProfileSection
            id="otp-section-travelers"
            title="נוסעים"
            icon={FaBed}
          >
            <PassengersData
              allDisabled={disabled}
              returnDate={hotel?.checkOutDate || undefined}
              isTour
            />
          </OrganizedTourProfileSection>

          {hasLdsSupplements ? (
            <OrganizedTourProfileSection
              id="otp-section-supplements"
              title={t("packages.supplement")}
              icon={FaBed}
            >
              <Supplement disabled={disabled} />
            </OrganizedTourProfileSection>
          ) : null}

          <OrganizedTourProfileSection
            id="otp-section-notes"
            title={t("packages.additionalInfo")}
            icon={FaCircleInfo}
          >
            <AdditionalInfo innerHtml={clientRemarks} />
          </OrganizedTourProfileSection>

          <section className="otp-disclosure" aria-labelledby="otp-disclosure-title">
            <h2 id="otp-disclosure-title" className="otp-disclosure__title">
              גילוי נאות וביטולים
            </h2>
            {DISCLOSURE_ITEMS.map((text) => (
              <p key={text} className="otp-disclosure__item">
                <strong>•</strong> {text}
              </p>
            ))}
          </section>

          <div className="otp-terms">
            <label className="otp-terms__label">
              <input
                type="checkbox"
                className="passangerData-checkout-checkbox"
                required
                onChange={(e) => setIsConditionsConfirmed(e.target.checked)}
                checked={isConditionsConfirmed}
              />
              <span>{t("completepassengers.approve")}</span>
              <button
                type="button"
                className="otp-terms__link"
                onClick={() => setIsTermsModal(true)}
              >
                תנאי ביטול ותקנון
              </button>
            </label>
          </div>
        </OrganizedTourProfileLayout>

        {isTermsModal ? (
          <div className="passangerData-popup-wrapper">
            <div ref={modalRef} className="passangerData-popup-frame tw-pt-4">
              <TermsAndConditions />
              <div className="passangerData-popup-agree-btn-wrapper">
                <button
                  type="button"
                  className="passangerData-popup-agree-btn"
                  onClick={() => setIsTermsModal(false)}
                >
                  {t("completepassengers.close")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  },
);

export default PackageDetails;
